import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import dxDataGrid from 'devextreme/ui/data_grid';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { TabRole, TabsService } from 'src/app/libs/components/tabs-host/tabs.service';
import { AclService } from 'src/app/libs/services/acl.service';
import { Format, Overhead, Plant, ProcessSheets, Section, TId, Workcenter } from 'src/app/models';
import { ICRUDResource } from 'src/app/resources';
import { MasterApiService } from 'src/app/services/master-api.service';
import { OverheadsEditComponent } from 'src/app/routes/aps/aps-configuration/overheads/overheads-edit/overheads-edit.component';
import { ProcessSheetOverheadsAddComponent } from './process-sheet-overheads-add/process-sheet-overheads-add.component';
import { ProcessSheetOverheadsResource } from './process-sheet-overheads.resource';

@Component({
  selector: 'app-process-sheet-overheads',
  templateUrl: './process-sheet-overheads.component.html',
  styleUrl: './process-sheet-overheads.component.sass',
  standalone: false,
})
export class ProcessSheetOverheadsComponent implements OnInit, OnDestroy {
  @Input() processSheet?: ProcessSheets;
  @Input() isDraft?: boolean;

  overheadsResource?: ICRUDResource<Overhead>;
  overheadsAddComponent = ProcessSheetOverheadsAddComponent;

  canWriteOverheads = false;
  plants: Plant[] = [];
  sections: Section[] = [];
  workcenters: Workcenter[] = [];
  formats: Format[] = [];

  private grid?: dxDataGrid;
  private linkResource?: ProcessSheetOverheadsResource;
  private destroy$ = new Subject<void>();

  addCustomParams: Record<string, unknown> = {};

  constructor(
    private api: MasterApiService,
    private acl: AclService,
    private tabs: TabsService,
  ) {}

  async ngOnInit(): Promise<void> {
    if (!this.processSheet?.processSheetId) return;

    this.linkResource = new ProcessSheetOverheadsResource(this.api, this.processSheet.processSheetId);
    await this.linkResource.reload();
    this.overheadsResource = this.linkResource;
    this.updateAddCustomParams();

    this.canWriteOverheads = this.acl.isResourceWritable(this.api.resources.overheads);

    this.tabs.lastTabClosed$.pipe(takeUntil(this.destroy$)).subscribe(() => this.reloadGrid());

    const companyId = this.api.auth.userValue!.lastCompanyId;
    const [plants, sections, workcenters, formats] = await Promise.all([
      firstValueFrom(this.api.resources.plants.getAll({ companyId })),
      firstValueFrom(this.api.resources.sections.getAll({ companyId })),
      firstValueFrom(this.api.resources.workcenters.getAll({ companyId })),
      firstValueFrom(this.api.resources.formats.getAll()),
    ]);
    this.plants = plants;
    this.sections = sections;
    this.workcenters = workcenters;
    this.formats = formats;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onGridInitialized(grid: dxDataGrid): void {
    this.grid = grid;
  }

  private updateAddCustomParams(): void {
    this.addCustomParams = {
      processSheetId: this.processSheet?.processSheetId,
      plantId: this.processSheet?.plantId,
      linkedOverheadIds: this.linkResource?.linkedIds ?? [],
    };
  }

  openOverhead(row: Overhead): void {
    if (!this.canWriteOverheads) return;
    const overheadId: TId | undefined = row?.id;
    if (!overheadId) return;

    const tab = this.tabs.createTab(OverheadsEditComponent, TabRole.EDITION, { inputPrms: { id: overheadId } });
    tab.component.instance.entityEvent$.pipe(takeUntil(tab.destroy$), takeUntil(this.destroy$)).subscribe(() => {
      this.reloadGrid();
    });
    tab.component.instance.entityChanged.pipe(takeUntil(tab.destroy$), takeUntil(this.destroy$)).subscribe(() => {
      this.reloadGrid();
    });
  }

  async reloadGrid(): Promise<void> {
    await this.linkResource?.reload();
    this.updateAddCustomParams();
    queueMicrotask(() => {
      try {
        this.grid?.getDataSource()?.reload();
      } catch {
        // ignore
      }
    });
  }
}
